import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import ReactCrop, {
	convertToPixelCrop,
	Crop,
	makeAspectCrop,
	PixelCrop,
} from 'react-image-crop';

import { Icons, SpinnerOverlay } from '@components/common';
import Dropzone from '@components/common/Dropzone';
import NavigateButtons from '@components/common/NavigateButtons';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { canvasPreview, generateCanvas, getBase64, useDebounceEffect } from '@utils/imageConverter';

import { IFile } from '@interfaces/common/file';
import { IErrorsType } from '@interfaces/import/import';

import { EMIMETypes, ESnackbarTypes, TMIMETypes } from '@enums/common';

import { HUNDRED_PERCENT_LOADING } from '@constants/common';
import {
	acceptedFileTypes,
	ASPECT,
	DEBOUNCE_TIMEOUT,
	DEFAULT_HEIGHT,
	DEFAULT_WIDTH,
	fileErrors,
	MAX_BYTES,
	ROTATE,
	SCALE,
	supportsText,
} from './constants';

import 'react-image-crop/src/ReactCrop.scss';
import styles from './UploadLogo.module.scss';

export type TCreateUploadUrl = (props: IFile) => void;

interface IUploadLogoProps {
	toggleLogoModal: () => void;
	isLoadingCreateUploadUrl: boolean;
	createUploadUrl: TCreateUploadUrl;
	circularCrop?: boolean;
	progress: number;
	unit?: 'px' | '%';
	maxHeight?: number;
	maxWidth?: number;
	cropWidth?: number;
	cropHeight?: number;
	maxSize?: number;
	acceptedLogoFileTypes?: Partial<Record<TMIMETypes, string[]>>;
	supportsLogoText?: string;
	logoFileErrors?: IErrorsType;
	isWithDisabledSubmit?: boolean;
	setIsDirtyUploadModal?: Dispatch<SetStateAction<boolean>>;
	maxSizeInfoText?: string;
}

const UploadLogo = ({
	toggleLogoModal,
	createUploadUrl,
	progress,
	isLoadingCreateUploadUrl,
	unit = '%',
	cropWidth = DEFAULT_WIDTH,
	cropHeight = DEFAULT_HEIGHT,
	circularCrop = true,
	maxHeight,
	maxWidth,
	maxSize = MAX_BYTES,
	acceptedLogoFileTypes = acceptedFileTypes,
	supportsLogoText = supportsText,
	logoFileErrors = fileErrors,
	isWithDisabledSubmit = false,
	setIsDirtyUploadModal,
	maxSizeInfoText,
}: IUploadLogoProps) => {
	const dispatch = useAppDispatch();

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [imgSrc, setImgSrc] = useState<string>('');
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
	const [crop, setCrop] = useState<Crop>();
	const isLoadingUploadLogo = (progress !== HUNDRED_PERCENT_LOADING) || isLoadingCreateUploadUrl;

	const previewCanvasRef = useRef(null);
	const imgRef = useRef<HTMLImageElement>(null);

	const setNewCompletedCrop = (newCompletedCrop: Crop) => {
		setCompletedCrop(convertToPixelCrop(newCompletedCrop, cropWidth, cropHeight));
	};

	const onImageLoad = (e: React.ChangeEvent<HTMLImageElement>) => {
		const { naturalWidth: width, naturalHeight: height } = e.currentTarget;

		const crop = makeAspectCrop(
			{
				unit: unit as any,
				width: cropWidth,
				height: cropHeight,
			},
			ASPECT,
			width,
			height,
		);

		setCrop(crop);
	};

	const handleCrop = (crop: Crop) => setCrop(crop);

	const selectFile = (files: Array<File>) => {
		setSelectedFile(files[0]);

		getBase64(files[0]).then((fileBase64) => {
			setImgSrc((fileBase64 as any).toString() || null);
		});
	};

	const clearSelectFile = () => {
		setImgSrc('');
		setSelectedFile(null);
	};

	const onUploadHandler = async () => {
		if (!completedCrop || !previewCanvasRef.current || !imgRef.current || !selectedFile) {
			dispatch(setSnackbar({
				message: 'Wrong file',
				type: ESnackbarTypes.error,
			}));

			return;
		}

		const previewCanvas: HTMLCanvasElement = previewCanvasRef.current;

		const offscreen = generateCanvas({ completedCrop, previewCanvas });

		try {
			const blob = await offscreen.convertToBlob({
				type: EMIMETypes.png,
			});

			if (blob) {
				const file = new File([blob], selectedFile.name, { type: selectedFile.type });
				createUploadUrl({ file });
			}
		} catch (error) {
			throw new Error(error as string);
		}
	};

	const isDisabledNext = isWithDisabledSubmit || isLoadingUploadLogo || !selectedFile;

	useEffect(() => {
		if (setIsDirtyUploadModal && !isDisabledNext) setIsDirtyUploadModal(true);
	}, [isDisabledNext, setIsDirtyUploadModal]);

	useDebounceEffect(
		async () => {
			try {
				if (
					completedCrop?.width &&
					completedCrop?.height &&
					imgRef.current &&
					previewCanvasRef.current
				) {
					const canvas: HTMLCanvasElement = previewCanvasRef.current;

					canvas.width = completedCrop.width;
					canvas.height = completedCrop.height;
					canvas.style.width = `${completedCrop.width}px`;
					canvas.style.height = `${completedCrop.height}px`;

					await canvasPreview(
						imgRef.current,
						canvas,
						completedCrop,
						SCALE,
						ROTATE,
					);
				}
			} catch (error) {
				dispatch(setSnackbar({
					message: (error as Error).message,
					type: ESnackbarTypes.error,
				}));
			}
		},
		DEBOUNCE_TIMEOUT,
		[completedCrop],
	);

	return (
		<div className={styles.uploadLogoContainer}>
			{isLoadingUploadLogo && <SpinnerOverlay />}
			<Dropzone
				acceptedFileTypes={acceptedLogoFileTypes}
				chooseFileText='Choose an image'
				errors={logoFileErrors}
				fileRejectionErrorHandler={clearSelectFile}
				maxFiles={1}
				maxSize={maxSize}
				maxSizeInfoText={maxSizeInfoText}
				onDropAcceptedHandler={clearSelectFile}
				setSelectedFiles={selectFile}
				supportsText={supportsLogoText}
				uploadIcon={Icons.uploadImage}
				uploadedIcon={Icons.uploadImage}
			/>
			<div className={styles.cropContainer}>
				{!!imgSrc
					&& <ReactCrop
						aspect={ASPECT}
						circularCrop={circularCrop}
						crop={crop}
						maxHeight={maxHeight}
						maxWidth={maxWidth}
						onChange={handleCrop}
						onComplete={setNewCompletedCrop}
					>
						<img
							ref={imgRef}
							alt='Crop me'
							onLoad={onImageLoad}
							src={imgSrc}
						/>
					</ReactCrop>
				}
			</div>
			{!!completedCrop && (
				<canvas
					ref={previewCanvasRef}
					style={{
						display: 'none',
						objectFit: 'contain',
					}}
				/>
			)}
			<div className={styles.buttons}>
				<NavigateButtons
					disabledNext={isDisabledNext}
					nextText='Upload'
					onCloseHandler={toggleLogoModal}
					onNextHandler={onUploadHandler}
				/>
			</div>
		</div>
	);
};

export default UploadLogo;
